/**
 * CensusRegistry.tsx — Browse every unique physical outlet in the registry.
 *
 * Rebuilt in Pass 3.
 * Preserves: server pagination, search + region filter, admin-only source popover,
 * admin-only export.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import {
  PageHeader,
  Button, Icon,
  SearchInput, Select,
  Table, TableToolbar, Pagination,
  EmptyState,
  toast,
} from '../../components/ui';

interface OutletRow {
  census_id: string;
  name: string;
  address: string;
  city: string;
  region: string;
  country: string;
  outlet_format: string;
  enrichment_score: number | null;
}

interface Filters {
  regions: string[];
  countries: string[];
  outlet_formats: string[];
  chains: string[];
}

const LIMIT = 25;

export default function CensusRegistry() {
  const navigate = useNavigate();
  const { modeVersion } = useAdmin();

  const [items, setItems] = useState<OutletRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [country, setCountry] = useState('');
  const [filters, setFilters] = useState<Filters>({ regions: [], countries: [], outlet_formats: [], chains: [] });
  const [sortCol, setSortCol] = useState('census_id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [exporting, setExporting] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    document.title = 'Census Database | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  const fetchData = useCallback(async (p: number, signal?: AbortSignal) => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(p), limit: String(LIMIT) });
    if (search) params.set('search', search);
    if (country) params.set('country', country);
    params.set('sort_col', sortCol);
    params.set('sort_dir', sortDir);
    try {
      const res = await api.get(`/api/registry/outlets?${params}`, { signal });
      setItems(res.data.items);
      setTotal(res.data.total);
      setPage(res.data.page);
      setPages(res.data.pages);
    } catch (err: unknown) {
      if (err instanceof Error && (err.name === 'CanceledError' || ('code' in err && (err as { code?: string }).code === 'ERR_CANCELED'))) return;
      toast.error('Failed to load registry');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, country, sortCol, sortDir]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(1, controller.signal);
    return () => controller.abort();
  }, [fetchData, modeVersion]);

  useEffect(() => {
    api.get<Filters>('/api/registry/filters').then(r => setFilters(r.data)).catch(() => {});
  }, [modeVersion]);

  // Debounced search
  const handleSearchInput = (val: string) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val.trim()); }, 300);
  };

  // Admin export
  const handleRegistryExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/api/export/registry', { responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = href;
      link.download = `meridian_registry_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(href);
      toast.success('Registry export ready', `${total.toLocaleString()} outlets downloaded`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      key: 'census_id',
      header: 'Census ID',
      width: 130,
      sortable: true,
      render: (r: OutletRow) => (
        <span
          onClick={(e) => { e.stopPropagation(); navigate(`/outlet/${r.census_id}`); }}
          style={{
            color: theme.brand.cyanLight,
            cursor: 'pointer',
            fontFamily: theme.font.data,
            fontSize: theme.fontSize.sm,
          }}
          onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
        >
          {r.census_id}
        </span>
      ),
    },
    { key: 'name', header: 'Outlet Name', sortable: true, render: (r: OutletRow) => r.name || null },
    { key: 'address', header: 'Address', sortable: true, render: (r: OutletRow) => <span style={{ color: theme.text.secondary }}>{r.address || '—'}</span> },
    { key: 'city', header: 'City', sortable: true, render: (r: OutletRow) => <span style={{ color: theme.text.secondary }}>{r.city || '—'}</span> },
    { key: 'country', header: 'Country', sortable: true, render: (r: OutletRow) => <span style={{ color: theme.text.secondary }}>{r.country || '—'}</span> },
    { key: 'outlet_format', header: 'Outlet Format', sortable: true, render: (r: OutletRow) => <span style={{ color: theme.text.secondary }}>{r.outlet_format || '—'}</span> },
    {
      key: 'enrichment_score',
      header: 'Enrichment Score',
      sortable: true,
      align: 'right' as const,
      width: 140,
      render: (r: OutletRow) => (
        <span style={{
          color: theme.text.secondary,
          fontFamily: theme.font.data,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {r.enrichment_score != null ? r.enrichment_score.toFixed(1) : '—'}
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: `${theme.space[8]} ${theme.space[10]}`, maxWidth: 1600, margin: '0 auto' }}>
      <PageHeader
        title="Census Database"
        description="Every unique physical outlet in the Meridian Registry."
        meta={
          <>
            <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.medium }}>
              {total.toLocaleString()}
            </span>
            <span>outlets</span>
          </>
        }
        actions={
          <Button
            variant="secondary"
            leftIcon={<Icon.Download size={14} />}
            onClick={handleRegistryExport}
            loading={exporting}
          >
            Export Registry
          </Button>
        }
      />

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: theme.space[3],
        marginBottom: theme.space[5],
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 280px', maxWidth: 400 }}>
          <SearchInput
            placeholder="Search by name, address, city, Census ID…"
            value={searchInput}
            onChange={e => handleSearchInput(e.target.value)}
            kbd={undefined}
          />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select value={country} onChange={e => setCountry(e.target.value)}>
            <option value="">All Countries</option>
            {filters.countries.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
      </div>

      {/* Toolbar */}
      <TableToolbar>
        <div style={{ color: theme.text.tertiary, fontSize: theme.fontSize.sm }}>
          Showing{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {items.length.toLocaleString()}
          </span>
          {' '}of{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {total.toLocaleString()}
          </span>
        </div>
        <Pagination page={page} totalPages={pages} onChange={p => fetchData(p)} />
      </TableToolbar>

      <Table
        data={items}
        columns={columns}
        loading={loading}
        minWidth={1100}
        rowKey={r => r.census_id}
        sortKey={sortCol}
        sortDir={sortDir}
        onSort={(key, dir) => { setSortCol(key); setSortDir(dir); }}
        emptyState={
          <EmptyState
            icon={<Icon.Database size={18} />}
            title="No outlets found"
            description="Try clearing filters or adjusting your search."
            action={(search || country) ? (
              <Button variant="secondary" size="sm"
                onClick={() => { setSearchInput(''); setSearch(''); setCountry(''); }}>
                Clear filters
              </Button>
            ) : undefined}
          />
        }
      />
    </div>
  );
}

